import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import VesselSelector from './components/VesselSelector';
import PerformanceMetrics from './components/PerformanceMetrics';
import FuelConsumptionChart from './components/FuelConsumptionChart';
import CIIRatingTracker from './components/CIIRatingTracker';
import OptimizationRecommendations from './components/OptimizationRecommendations';
import DataIntegrationPanel from './components/DataIntegrationPanel';
import VoyageHistoryTable from './components/VoyageHistoryTable';
import AlertsNotifications from './components/AlertsNotifications';

const VesselPerformance = () => {
  const [selectedVessel, setSelectedVessel] = useState('');
  const [loading, setLoading] = useState(true);

  // Mock data for vessels
  const vessels = [
    {
      id: 'mv-atlantic-voyager',
      name: 'MV Atlantic Voyager',
      imo: '9876543',
      type: 'Container Ship',
      flag: 'Panama',
      yearBuilt: 2018,
      dwt: 14500,
      status: 'active'
    },
    {
      id: 'mv-pacific-trader',
      name: 'MV Pacific Trader',
      imo: '9765432',
      type: 'Bulk Carrier',
      flag: 'Liberia',
      yearBuilt: 2020,
      dwt: 82000,
      status: 'active'
    },
    {
      id: 'mv-nordic-spirit',
      name: 'MV Nordic Spirit',
      imo: '9654321',
      type: 'Tanker',
      flag: 'Marshall Islands',
      yearBuilt: 2019,
      dwt: 115000,
      status: 'active'
    }
  ];

  // Mock performance data
  const performanceData = {
    fuelEfficiency: 42.3,
    ciiRating: 'B',
    emissionsIntensity: 8.7,
    speedOptimization: 87.2
  };

  // Mock fuel consumption data
  const fuelData = Array.from({ length: 30 }, (_, i) => ({
    timestamp: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000),
    actualConsumption: 38 + Math.random() * 8,
    predictedConsumption: 40 + Math.random() * 6,
    optimalConsumption: 35 + Math.random() * 4,
    speed: 12 + Math.random() * 3
  }));

  // Mock CII data
  const ciiData = Array.from({ length: 12 }, (_, i) => {
    const ratings = ['A', 'B', 'C', 'D', 'E'];
    const baseValue = 8.5;
    const variation = (Math.random() - 0.5) * 2;
    const ciiValue = baseValue + variation;
    
    let rating = 'C';
    if (ciiValue < 7) rating = 'A';
    else if (ciiValue < 8) rating = 'B';
    else if (ciiValue > 10) rating = 'E';
    else if (ciiValue > 9) rating = 'D';

    return {
      period: `2024-${String(i + 1)?.padStart(2, '0')}`,
      rating,
      ciiValue,
      threshold: 9.0,
      target: 8.0
    };
  });

  // Mock optimization recommendations
  const recommendations = [
    {
      id: 1,
      category: 'speed',
      priority: 'high',
      title: 'Optimize Cruising Speed',
      description: 'Reduce average speed by 1.2 knots to improve fuel efficiency and reduce emissions intensity.',
      potentialSavings: 8500,
      co2Impact: 12.3,
      implementationTime: '2-3 days',
      steps: [
        'Adjust voyage planning parameters',
        'Communicate new speed profile to bridge team',
        'Monitor fuel consumption for 48 hours',
        'Fine-tune based on weather conditions'
      ]
    },
    {
      id: 2,
      category: 'route',
      priority: 'medium',
      title: 'Weather Routing Optimization',
      description: 'Implement advanced weather routing to avoid adverse conditions and optimize fuel consumption.',
      potentialSavings: 6200,
      co2Impact: 8.7,
      implementationTime: '1 week',
      steps: [
        'Subscribe to enhanced weather data service',
        'Install weather routing software',
        'Train navigation officers',
        'Implement routing recommendations'
      ]
    },
    {
      id: 3,
      category: 'fuel',
      priority: 'high',
      title: 'Fuel Quality Management',
      description: 'Optimize fuel procurement and blending to improve combustion efficiency.',
      potentialSavings: 12400,
      co2Impact: 15.2,
      implementationTime: '1 month',
      steps: [
        'Analyze current fuel suppliers',
        'Implement fuel quality testing',
        'Optimize fuel blending ratios',
        'Monitor engine performance'
      ]
    },
    {
      id: 4,
      category: 'maintenance',
      priority: 'medium',
      title: 'Hull Cleaning Schedule',
      description: 'Optimize hull cleaning frequency to maintain optimal hydrodynamic performance.',
      potentialSavings: 4800,
      co2Impact: 6.5,
      implementationTime: '2 weeks',
      steps: [
        'Assess current hull condition',
        'Schedule underwater inspection',
        'Plan cleaning at next suitable port',
        'Implement performance monitoring'
      ]
    }
  ];

  // Mock voyage history
  const voyageHistory = [
    {
      id: 1,
      voyageNumber: 'ATL-001-2024',
      route: 'Hamburg - New York',
      charterer: 'Maersk Line',
      departureDate: '2024-08-15',
      arrivalDate: '2024-08-28',
      duration: 13,
      fuelConsumption: 520,
      co2Emissions: 1638,
      emissionsIntensity: 8.2,
      ciiRating: 'B',
      complianceStatus: 'compliant'
    },
    {
      id: 2,
      voyageNumber: 'ATL-002-2024',
      route: 'New York - Rotterdam',
      charterer: 'MSC',
      departureDate: '2024-07-20',
      arrivalDate: '2024-08-02',
      duration: 13,
      fuelConsumption: 485,
      co2Emissions: 1527,
      emissionsIntensity: 7.9,
      ciiRating: 'B',
      complianceStatus: 'compliant'
    },
    {
      id: 3,
      voyageNumber: 'ATL-003-2024',
      route: 'Rotterdam - Felixstowe',
      charterer: 'CMA CGM',
      departureDate: '2024-07-05',
      arrivalDate: '2024-07-07',
      duration: 2,
      fuelConsumption: 95,
      co2Emissions: 299,
      emissionsIntensity: 9.1,
      ciiRating: 'C',
      complianceStatus: 'warning'
    }
  ];

  useEffect(() => {
    // Set default vessel selection
    if (vessels?.length > 0 && !selectedVessel) {
      setSelectedVessel(vessels?.[0]?.id);
    }
    
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [vessels, selectedVessel]);

  const selectedVesselData = vessels?.find(v => v?.id === selectedVessel);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Vessel Performance - Carbon Cost Router</title>
          <meta name="description" content="Monitor vessel performance, fuel efficiency, and emissions data with real-time IoT integration" />
        </Helmet>
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading vessel performance data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Vessel Performance - Carbon Cost Router</title>
        <meta name="description" content="Monitor vessel performance, fuel efficiency, and emissions data with real-time IoT integration" />
      </Helmet>
      
      <Header />
      
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Vessel Performance Analytics
            </h1>
            <p className="text-lg text-muted-foreground">
              Real-time monitoring and optimization for maritime operations
            </p>
          </div>

          <div className="space-y-8">
            {/* Vessel Selection */}
            <VesselSelector
              selectedVessel={selectedVessel}
              onVesselChange={setSelectedVessel}
              vessels={vessels}
            />

            {selectedVesselData && (
              <>
                {/* Performance Metrics */}
                <PerformanceMetrics
                  vessel={selectedVesselData}
                  performanceData={performanceData}
                />

                {/* Alerts and Notifications */}
                <AlertsNotifications vessel={selectedVesselData} />

                {/* Charts Row */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  <FuelConsumptionChart
                    vessel={selectedVesselData}
                    fuelData={fuelData}
                  />
                  <CIIRatingTracker
                    vessel={selectedVesselData}
                    ciiData={ciiData}
                  />
                </div>

                {/* Optimization Recommendations */}
                <OptimizationRecommendations
                  vessel={selectedVesselData}
                  recommendations={recommendations}
                />

                {/* Data Integration Panel */}
                <DataIntegrationPanel vessel={selectedVesselData} />

                {/* Voyage History */}
                <VoyageHistoryTable
                  vessel={selectedVesselData}
                  voyageHistory={voyageHistory}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VesselPerformance;